import { useCallback, useContext, useRef, useState } from "react";

import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";

import { QuoteDetailsContext } from "../context/quote-details-context.tsx";
import {
	getLtlFallbackMessage,
	QUOTE_REFRESH_REQUIRED_STATUS,
} from "../utils/ltl-error-handler.ts";
import { useRefreshConflictedQuote } from "./use-shipment-summary-hooks.tsx";

const REFRESH_QUOTE_MESSAGE =
	"The quote has recently been updated by another user. Please select Refresh below to get the latest information.";

/**
 * Actions that can trigger the refresh flow. The value doubles as the verb
 * inserted into the refresh-success notice ("Please review and {action}
 * again."), so each entry must read naturally in that sentence.
 */
export type LtlRefreshAction =
	| "save"
	| "book"
	| "tender"
	| "add insurance"
	| "remove insurance"
	| "select a carrier";

/**
 * Builds the post-refresh success notice. When no action is known, falls back
 * to the generic "try again" copy.
 */
const getRefreshSuccessMessage = (action: LtlRefreshAction | null) =>
	`The quote has been refreshed with the latest information. Please review and ${action ?? "try"} again.`;

/**
 * Failure-shape input accepted by `handleLtlActionError`. Matches the
 * `{ success: false }` branch of `ApiResult` plus an optional HTTP status
 * (populated by `createLtlMutationErrorHandler`).
 */
export type LtlActionFailure = {
	success: false;
	error?: string;
	status?: number;
};

/**
 * React hook mirroring the legacy `BaseActionComponent.handleErrorModals`.
 *
 * Inspects the `{ success: false }` result of an LTL mutation and:
 *   - On HTTP 412, opens the "Refresh Quote" confirmation modal. Confirming
 *     calls the GET-quote endpoint and rehydrates `QuoteDetailsContext`.
 *   - On any other failure, shows the extracted error message in a toast
 *     notification (`useNotificationActions().showError`).
 *
 * Returns `refreshModal` (JSX to render in the component tree) and
 * `isRefreshing` (suitable for spinner gating).
 */
export const useLtlActionErrorHandler = () => {
	const { quoteState } = useContext(QuoteDetailsContext);
	const { isInternal } = useDeploymentType();
	const fallbackMessage = getLtlFallbackMessage(isInternal);
	const { refreshQuote, isRefreshing } = useRefreshConflictedQuote();
	const { showError, showInfo } = useNotificationActions();
	const [isRefreshModalOpen, setIsRefreshModalOpen] = useState(false);

	const pendingActionRef = useRef<LtlRefreshAction | null>(null);

	const handleRefreshConfirmed = useCallback(async () => {
		const action = pendingActionRef.current;
		pendingActionRef.current = null;
		setIsRefreshModalOpen(false);
		const result = await refreshQuote(quoteState.id);
		if (!result.success) {
			showError(result.error || fallbackMessage);
			return;
		}
		showInfo(getRefreshSuccessMessage(action));
	}, [refreshQuote, quoteState.id, showError, showInfo, fallbackMessage]);

	const handleRefreshCancelled = useCallback(() => {
		pendingActionRef.current = null;
		setIsRefreshModalOpen(false);
	}, []);

	const handleLtlActionError = useCallback(
		(failure: LtlActionFailure, action?: LtlRefreshAction) => {
			if (failure.status === QUOTE_REFRESH_REQUIRED_STATUS) {
				pendingActionRef.current = action ?? null;
				setIsRefreshModalOpen(true);
				return;
			}
			showError(failure.error || fallbackMessage);
		},
		[showError, fallbackMessage],
	);

	const refreshModal = isRefreshModalOpen ? (
		<TraxModal
			cancelText={"Cancel"}
			confirmText={"Refresh"}
			onCancel={handleRefreshCancelled}
			onConfirm={() => void handleRefreshConfirmed()}
			showButtonsDisabled={isRefreshing}
			title={"Refresh Quote"}
			type={"confirmation"}
		>
			<p>{REFRESH_QUOTE_MESSAGE}</p>
		</TraxModal>
	) : null;

	return {
		handleLtlActionError,
		refreshModal,
		isRefreshing,
	};
};
